/**
 * abilityVfxJax — Jax Q/W/E/R lamppost silhouettes (SPY-32).
 */
export function buildLeapStrike(THREE, spec) {
  const group = new THREE.Group();
  const s = 0.5 * (spec.scale || 1.15);
  const purple = mat(THREE, spec.color || 0xa855f7, 0.9);
  const pale = mat(THREE, 0xe9d5ff, 0.92);
  addLeapArc(THREE, group, s, purple, pale);
  group.userData = { shape: 'leap_strike' };
  return group;
}

export function buildEmpower(THREE, spec) {
  const group = new THREE.Group();
  const s = 0.5 * (spec.scale || 1.2);
  const gold = mat(THREE, spec.color || 0xf59e0b, 0.9);
  const pale = mat(THREE, 0xfef3c7, 0.92);
  addStaffGlow(THREE, group, s, gold, pale);
  addFootRing(THREE, group, s, gold);
  group.userData = { shape: 'empower' };
  return group;
}

export function buildCounterStrike(THREE, spec) {
  const group = new THREE.Group();
  const s = spec.scale || 1.55;
  const purple = mat(THREE, spec.color || 0x7c3aed, 0.88);
  const pale = mat(THREE, 0xc4b5fd, 0.9);
  addSpinStaffs(THREE, group, s, purple, pale);
  addDodgeRing(THREE, group, s, purple);
  group.userData = { shape: 'counter_strike' };
  return group;
}

export function buildGrandmasterArms(THREE, spec) {
  const group = new THREE.Group();
  const s = 0.55 * (spec.scale || 1.7);
  const gold = mat(THREE, spec.color || 0xeab308, 0.9);
  const purple = mat(THREE, 0x7c3aed, 0.85);
  addAuraRings(THREE, group, s, gold, purple);
  addStaffCrest(THREE, group, s, gold, purple);
  group.userData = { shape: 'grandmaster_arms' };
  return group;
}

function addLeapArc(THREE, group, s, purple, pale) {
  const arc = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.45, s * 0.85, 16).rotateX(-Math.PI / 2),
    purple
  );
  arc.position.y = 0.35 * s;
  group.add(arc);
  const staff = new THREE.Mesh(new THREE.CylinderGeometry(0.05 * s, 0.05 * s, 1.1 * s, 8), pale);
  staff.position.y = 0.75 * s;
  group.add(staff);
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.14 * s, 0.3 * s, 5), purple);
  tip.position.y = 1.35 * s;
  group.add(tip);
  const trail = new THREE.Mesh(new THREE.BoxGeometry(0.08 * s, 0.05 * s, 0.7 * s), pale);
  trail.position.set(0, 0.4 * s, -0.35 * s);
  group.add(trail);
}

function addStaffGlow(THREE, group, s, gold, pale) {
  const staff = new THREE.Mesh(new THREE.BoxGeometry(0.08 * s, 1.1 * s, 0.08 * s), pale);
  staff.position.y = 0.75 * s;
  group.add(staff);
  const glow = new THREE.Mesh(new THREE.SphereGeometry(0.2 * s, 10, 10), gold);
  glow.position.y = 1.25 * s;
  group.add(glow);
  const spark = new THREE.Mesh(new THREE.SphereGeometry(0.08 * s, 6, 6), pale);
  spark.position.set(0.2 * s, 1.15 * s, 0);
  group.add(spark);
}

function addFootRing(THREE, group, s, gold) {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.4, s * 0.95, 18).rotateX(-Math.PI / 2),
    gold
  );
  ring.position.y = 0.05;
  group.add(ring);
}

function addSpinStaffs(THREE, group, s, purple, pale) {
  for (let i = 0; i < 4; i += 1) {
    const staff = new THREE.Mesh(new THREE.BoxGeometry(s * 0.95, 0.07, 0.12), pale);
    staff.position.y = 0.5;
    if (!staff.rotation) staff.rotation = { y: 0 };
    staff.rotation.y = (Math.PI / 2) * i;
    group.add(staff);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.25, 4), purple);
    tip.position.set(
      Math.cos((Math.PI / 2) * i) * s * 0.5,
      0.5,
      Math.sin((Math.PI / 2) * i) * s * 0.5
    );
    group.add(tip);
  }
}

function addDodgeRing(THREE, group, s, purple) {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.3, s * 0.55, 22).rotateX(-Math.PI / 2),
    purple
  );
  ring.position.y = 0.05;
  group.add(ring);
}

function addAuraRings(THREE, group, s, gold, purple) {
  const outer = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.65, s * 1.1, 26).rotateX(-Math.PI / 2),
    gold
  );
  outer.position.y = 0.05;
  group.add(outer);
  const inner = new THREE.Mesh(
    new THREE.RingGeometry(s * 0.3, s * 0.55, 20).rotateX(-Math.PI / 2),
    purple
  );
  inner.position.y = 0.08;
  group.add(inner);
}

function addStaffCrest(THREE, group, s, gold, purple) {
  const staff = new THREE.Mesh(new THREE.BoxGeometry(0.1 * s, 1.2 * s, 0.1 * s), gold);
  staff.position.y = 0.8 * s;
  group.add(staff);
  const crest = new THREE.Mesh(new THREE.SphereGeometry(0.22 * s, 10, 10), purple);
  crest.position.y = 1.45 * s;
  group.add(crest);
  const gem = new THREE.Mesh(new THREE.ConeGeometry(0.12 * s, 0.28 * s, 5), gold);
  gem.position.y = 1.7 * s;
  group.add(gem);
}

function mat(THREE, color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    side: THREE.DoubleSide
  });
}
